import { IconName, TAGButton, TAGSvgIcon } from '@aspendental/shared-components-web';
import MediaSlider from '@aspendental/shared-components-web/lib/components/ProductFeature/MediaSlider';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { IProviderDetailsPage } from './ProviderDetailsPage';
import { getProviderEducationDetails, getProviderHeroTitle, getProviderHireDate, getProviderLanguages } from './ProviderDetailsPage.helpers';
import { getHeroContainerStyles, getHeroContentContainerStyles, getHeroImageWrapperStyles, getHeroLabelsButtonStyles, getHeroLabelsItemStyles, getHeroLabelsListStyles, heroQuoteStyles, heroTitleStyles } from './ProviderDetailsPage.styles';
import { Asset, Maybe, MediaSliderContentsItem } from '@src/lib/__generated/sdk';
interface IHeroLabel {
  label: string;
  icon: IconName;
  href?: string;
  target?: string;
}
export default function ProviderHero({
  provider,
  config
}: IProviderDetailsPage) {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('md'));
  const providerHeroTheme = config?.featureFlags?.providerHeroTheme || 'dark';
  const withLeadingProviderAssociations = config?.featureFlags?.withLeadingProviderAssociations || false;
  const mainLocation = provider.providerLocations?.find(({
    isMain
  }) => isMain);
  const mainAddress = mainLocation?.facility?.location?.address1 || '';
  const mainCity = mainLocation?.facility?.location?.city || '';
  const mainState = mainLocation?.facility?.location?.stateCode || '';
  const mainZipCode = mainLocation?.facility?.location?.zipCode || '';
  const education = getProviderEducationDetails(provider);
  const hireDate = getProviderHireDate(provider);
  const languages = getProviderLanguages(provider);
  const languagesLabelsList: IHeroLabel[] = languages ? [{
    label: languages,
    icon: 'Language'
  }] : [];
  const associationLabelsList: IHeroLabel[] = provider.associations?.map(association => ({
    label: association ? association.trim() : '',
    icon: 'Verified'
  })) || [];
  const labelList: IHeroLabel[] = [...(mainLocation ? [{
    label: `${mainAddress} ${mainCity}, ${mainState}`,
    icon: 'LocationOn' as IconName,
    href: config.googleMapBusinessQuery.replace(/\[address\]/gi, `${mainAddress}, ${mainCity}, ${mainState} ${mainZipCode}`),
    target: '_blank'
  }] : []), ...(education ? [{
    label: education,
    icon: 'MenuBookIcon' as IconName
  }] : []), ...(withLeadingProviderAssociations ? associationLabelsList : []), ...(hireDate ? [{
    label: `With ${config.name} since ${hireDate}`,
    icon: 'Verified' as IconName
  }] : []), ...languagesLabelsList, ...(!withLeadingProviderAssociations ? associationLabelsList : [])];
  const textColor = providerHeroTheme === 'light' ? 'text.primary' : 'text.light';
  const hasProviderPhotos = !!provider.providerImages.length;
  return <Box data-test-id="provider_hero_container" sx={getHeroContainerStyles(providerHeroTheme, theme, hasProviderPhotos)} data-sentry-element="Box" data-sentry-component="ProviderHero" data-sentry-source-file="ProviderHero.tsx">
			<Box data-test-id="provider_hero_content_container" sx={getHeroContentContainerStyles(theme, hasProviderPhotos)} data-sentry-element="Box" data-sentry-source-file="ProviderHero.tsx">
				<Typography color={textColor} component="h1" variant={isMobileScreen ? 'heroMedium' : 'heroLarge'} textAlign={{
        xs: 'center',
        md: 'left'
      }} sx={heroTitleStyles} data-test-id="provider_hero_title" data-sentry-element="Typography" data-sentry-source-file="ProviderHero.tsx">
					{getProviderHeroTitle(provider)}
				</Typography>
				{provider.quote && <Typography color={textColor} variant="header4" sx={heroQuoteStyles} data-test-id="provider_hero_quote">
						{provider.quote}
					</Typography>}
				<Box sx={getHeroLabelsListStyles(hasProviderPhotos)} flexWrap="wrap" data-test-id="provider_hero_content_section" data-sentry-element="Box" data-sentry-source-file="ProviderHero.tsx">
					{labelList.map(({
          label,
          icon,
          href,
          target
        }) => {
          if (label) {
            if (href) {
              return <TAGButton key={label} size="small" variant="text" startIcon={<TAGSvgIcon icon={icon} size={20} />} sx={getHeroLabelsButtonStyles(textColor)} href={href} target={target}>
										{label}
									</TAGButton>;
            } else {
              return <Box sx={getHeroLabelsItemStyles(textColor)} key={label}>
										<TAGSvgIcon icon={icon} size={20} />
										<Typography color={textColor} variant="buttonLabel" width={'100%'}>
											{label}
										</Typography>
									</Box>;
            }
          }
        })}
				</Box>
			</Box>
			{provider.providerImages.length > 0 && <Box sx={getHeroImageWrapperStyles} data-test-id="provider_hero_images_slider">
					<MediaSlider fullHeight contentsCollection={{
        items: provider.providerImages.map(({
          url
        }, index) => ({
          _id: '',
          __typename: 'FocalPointImage',
          image: {
            url
          } as Asset,
          sys: {
            environmentId: `${index}`,
            id: `${index}`,
            spaceId: `${index}`
          },
          contentfulMetadata: {
            tags: [],
            concepts: []
          }
        })) as Maybe<MediaSliderContentsItem>[],
        limit: provider.providerImages.length,
        skip: 0,
        total: provider.providerImages.length
      }} />
				</Box>}
		</Box>;
}